import React, { useState, useCallback } from 'react';
import api from './api';

const QUERY_TYPES = {
  progress: {
    label: '📈 Progress',
    query: 'Give a comprehensive progress summary for this patient. Include session completion rate, symptom trends over time, and overall treatment trajectory.',
  },
  pain: {
    label: '😣 Pain Trends',
    query: 'Analyze pain and symptom trends across all recorded sessions. Identify patterns, escalations, and any concerning trends.',
  },
  compliance: {
    label: '🏋️ Compliance',
    query: 'How compliant has this patient been with their treatment plan? Analyze session attendance, completion rate, and any dropped or skipped sessions.',
  },
  recommendations: {
    label: '💡 Recommendations',
    query: 'What are the key clinical concerns and recommendations for this patient based on their progress data? Suggest next steps.',
  },
  rom: {
    label: '📐 ROM Progress',
    query: 'Summarize range of motion and muscle strength improvements from the assessment records. Highlight gains, plateaus, or regressions.',
  },
  custom: {
    label: '✏️ Custom',
    query: '',
  },
};

const PILL_STYLE = (active) => ({
  padding: '6px 14px',
  borderRadius: 20,
  border: active ? '2px solid #4f46e5' : '1.5px solid #d1d5db',
  background: active ? '#4f46e5' : '#fff',
  color: active ? '#fff' : '#374151',
  fontSize: 13,
  fontWeight: active ? 700 : 500,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export default function PatientAIReport({ patientCard }) {
  const [reportType,   setReportType]   = useState('progress');
  const [customQuery,  setCustomQuery]  = useState('');
  const [generating,   setGenerating]   = useState(false);
  const [response,     setResponse]     = useState('');
  const [meta,         setMeta]         = useState(null);   // { model, generatedAt }
  const [error,        setError]        = useState('');
  const [history,      setHistory]      = useState(null);   // null = not loaded yet
  const [showHistory,  setShowHistory]  = useState(false);
  const [copied,       setCopied]       = useState(false);

  const handleGenerate = useCallback(async () => {
    const finalQuery = reportType === 'custom'
      ? customQuery.trim()
      : QUERY_TYPES[reportType].query;

    if (!finalQuery) {
      setError('Please enter a question.');
      return;
    }
    setError('');
    setResponse('');
    setMeta(null);
    setGenerating(true);

    try {
      const res = await api.post(
        `/ai/patients/${patientCard}/report`,
        { query: finalQuery, reportType }
      );
      setResponse(res.data.response || '');
      setMeta({ model: res.data.model, generatedAt: res.data.generatedAt });
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Unknown error';
      setError(`Error: ${msg}`);
    } finally {
      setGenerating(false);
    }
  }, [patientCard, reportType, customQuery]);

  const handleCopy = () => {
    navigator.clipboard.writeText(response).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const handleToggleHistory = async () => {
    if (!showHistory && history === null) {
      try {
        const res = await api.get(`/ai/patients/${patientCard}/history`);
        setHistory(res.data || []);
      } catch {
        setHistory([]);
      }
    }
    setShowHistory(v => !v);
  };

  const handleLoadHistoryItem = (item) => {
    setResponse(item.preview.length < 300 ? item.preview : item.preview + '\n\n[truncated — regenerate for full report]');
    setMeta({ model: item.model, generatedAt: item.generatedAt });
    setReportType(item.reportType || 'custom');
    setCustomQuery(item.query || '');
    setShowHistory(false);
  };

  const fmtDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ fontFamily: 'system-ui, sans-serif', maxWidth: 860, margin: '0 auto', padding: '16px 4px' }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <span style={{ fontSize: 22 }}>🤖</span>
        <div>
          <div style={{ fontSize: 17, fontWeight: 700, color: '#1e1b4b' }}>AI Summary Report</div>
          <div style={{ fontSize: 12, color: '#6b7280' }}>Powered by Groq · data stays anonymised</div>
        </div>
      </div>

      {/* Query type pills */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
        {Object.entries(QUERY_TYPES).map(([key, { label }]) => (
          <button
            key={key}
            style={PILL_STYLE(reportType === key)}
            onClick={() => { setReportType(key); setError(''); }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Custom textarea */}
      {reportType === 'custom' && (
        <textarea
          value={customQuery}
          onChange={e => setCustomQuery(e.target.value)}
          placeholder="Enter your clinical question about this patient…"
          rows={3}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '10px 12px', borderRadius: 10,
            border: '1.5px solid #d1d5db', fontSize: 14,
            resize: 'vertical', marginBottom: 12,
            outline: 'none',
          }}
        />
      )}

      {/* Generate button */}
      <button
        onClick={handleGenerate}
        disabled={generating}
        style={{
          padding: '10px 24px', borderRadius: 10,
          background: generating ? '#a5b4fc' : '#4f46e5',
          color: '#fff', fontSize: 14, fontWeight: 700,
          border: 'none', cursor: generating ? 'default' : 'pointer',
          display: 'flex', alignItems: 'center', gap: 8,
          marginBottom: 16,
        }}
      >
        {generating ? (
          <>
            <span style={{ display: 'inline-block', animation: 'spin 1s linear infinite' }}>⏳</span>
            Analysing patient data…
          </>
        ) : (
          <> ✨ Generate Report </>
        )}
      </button>

      {/* Error */}
      {error && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5',
          color: '#b91c1c', borderRadius: 8, padding: '10px 14px',
          fontSize: 13, marginBottom: 12,
        }}>
          {error}
        </div>
      )}

      {/* Response box */}
      {response && (
        <div style={{
          background: '#fff', border: '1.5px solid #e5e7eb',
          borderRadius: 12, padding: '18px 20px', marginBottom: 14,
          boxShadow: '0 1px 4px rgba(0,0,0,.06)',
        }}>
          <pre style={{
            whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            fontSize: 13.5, lineHeight: 1.7,
            color: '#1f2937', margin: 0,
            fontFamily: 'inherit',
          }}>
            {response}
          </pre>

          {/* Footer row */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            marginTop: 14, paddingTop: 12,
            borderTop: '1px solid #f3f4f6',
            flexWrap: 'wrap',
          }}>
            <button
              onClick={handleCopy}
              style={{
                padding: '5px 14px', borderRadius: 7,
                border: '1.5px solid #d1d5db',
                background: copied ? '#f0fdf4' : '#f9fafb',
                color: copied ? '#15803d' : '#374151',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              {copied ? '✅ Copied' : '📋 Copy'}
            </button>
            <button
              onClick={handleGenerate}
              disabled={generating}
              style={{
                padding: '5px 14px', borderRadius: 7,
                border: '1.5px solid #d1d5db',
                background: '#f9fafb',
                color: '#374151',
                fontSize: 12, fontWeight: 600, cursor: 'pointer',
              }}
            >
              🔄 Regenerate
            </button>
            {meta && (
              <span style={{ fontSize: 11, color: '#9ca3af', marginLeft: 'auto' }}>
                {meta.model} · {fmtDate(meta.generatedAt)}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Past reports toggle */}
      <div>
        <button
          onClick={handleToggleHistory}
          style={{
            background: 'none', border: 'none',
            color: '#4f46e5', fontSize: 13, fontWeight: 600,
            cursor: 'pointer', padding: '4px 0',
            display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {showHistory ? '▲' : '▼'}
          Past Reports {history !== null && history.length > 0 ? `(${history.length})` : ''}
        </button>

        {showHistory && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {history === null || history.length === 0 ? (
              <div style={{ color: '#9ca3af', fontSize: 13, padding: '8px 0' }}>No past reports found.</div>
            ) : (
              history.map((item) => (
                <div
                  key={item.reportId}
                  onClick={() => handleLoadHistoryItem(item)}
                  style={{
                    background: '#f9fafb', border: '1px solid #e5e7eb',
                    borderRadius: 8, padding: '10px 14px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#4f46e5' }}>
                      {QUERY_TYPES[item.reportType]?.label || item.reportType}
                    </span>
                    <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(item.generatedAt)}</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {item.query}
                  </div>
                  <div style={{ fontSize: 12, color: '#374151', marginTop: 4, opacity: 0.7 }}>
                    {item.preview.slice(0, 120)}{item.preview.length > 120 ? '…' : ''}
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
